import { ref } from "vue";
import axios from "axios";
import { useDeviceMeasurementsStore } from "@/views/DeviceMeasurements/store";
import { DeviceMeasurement } from "@/views/DeviceMeasurements/models/deviceMeasurement";
import { useDeviceSettingsStore } from "@/views/DeviceSettings/store";
import { SensorSettings } from "@/views/SensorSettings/models/sensorSettings";
import { DeviceSettings } from "@/views/DeviceSettings/models/deviceSettings";

// Interfaces para los datos organizados
export interface DeviceSensorData {
  temperatureData: Array<{ timestamp: string; temperature: number }>;
  oxygenData: Array<{ timestamp: string; oxygen: number }>;
  batteryData: Array<{
    timestamp: string;
    batteryLevel: number;
    voltage?: number;
  }>;
  temperatureOxygenData: Array<{
    timestamp: string;
    temperature: number;
    oxygen: number;
  }>;
  lastValues: {
    temperature?: number;
    oxygen?: number;
    batteryLevel?: number;
    voltage?: number;
  };
}

export interface SensorMapping {
  temperature: string[];
  oxygen: string[];
  battery: string[];
  voltage: string[];
  humidity: string[]; // Para uso futuro
}

export function useDeviceSensorManager() {
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Lazy initialization of stores to avoid proxy issues
  const getStores = () => {
    const deviceMeasurementsStore = useDeviceMeasurementsStore();
    const deviceSettingsStore = useDeviceSettingsStore();
    return { deviceMeasurementsStore, deviceSettingsStore };
  };

  /**
   * Obtiene los sensor IDs organizados por tipo a partir de las configuraciones del dispositivo
   */
  const getSensorMapping = (deviceSettings: DeviceSettings): SensorMapping => {
    const mapping: SensorMapping = {
      temperature: [],
      oxygen: [],
      battery: [],
      voltage: [],
      humidity: [],
    };

    if (deviceSettings && deviceSettings.sensorSettings) {
      deviceSettings.sensorSettings.forEach((sensorSetting: SensorSettings) => {
        const sensorField = sensorSetting.sensorId.toLowerCase();

        // Mapear sensores por nombre/campo (puedes ajustar estos criterios)
        if (
          sensorField.includes("temp") ||
          sensorField.includes("temperatura")
        ) {
          mapping.temperature.push(sensorSetting.sensorId);
        } else if (
          sensorField.includes("ox") ||
          sensorField.includes("oxygen") ||
          sensorField.includes("oxigeno")
        ) {
          mapping.oxygen.push(sensorSetting.sensorId);
        } else if (
          sensorField.includes("batt") ||
          sensorField.includes("bateria") ||
          sensorField.includes("battery")
        ) {
          mapping.battery.push(sensorSetting.sensorId);
        } else if (
          sensorField.includes("volt") ||
          sensorField.includes("voltage") ||
          sensorField.includes("voltaje")
        ) {
          mapping.voltage.push(sensorSetting.sensorId);
        } else if (
          sensorField.includes("hum") ||
          sensorField.includes("humedad") ||
          sensorField.includes("humidity")
        ) {
          mapping.humidity.push(sensorSetting.sensorId);
        }
      });
    }

    return mapping;
  };

  /**
   * Carga todos los datos de sensores para un dispositivo específico
   */
  const loadDeviceSensorData = async (
    deviceId: string
  ): Promise<DeviceSensorData> => {
    loading.value = true;
    error.value = null;

    // console.log("[useDeviceSensorManager] loadDeviceSensorData - deviceId:", deviceId);

    try {
      // Usar la variable de entorno para la URL base
      const url = `${process.env.VUE_APP_URL_BASE}/devices/${deviceId}/device-measurements`;
      // console.log("[useDeviceSensorManager] Fetching URL:", url);
      // console.log("[useDeviceSensorManager] VUE_APP_URL_BASE:", process.env.VUE_APP_URL_BASE);
      const response = await axios.get(url);
      // console.log("[useDeviceSensorManager] Response status:", response.status);
      // console.log("[useDeviceSensorManager] Response data:", response.data);
      // console.log("[useDeviceSensorManager] Response data count:", response.data?.length || 0);
      // console.log("[useDeviceSensorManager] Response data sample:", response.data?.slice?.(0, 3));

      // Esperamos una lista de mediciones con shape:
      // { id, time, deviceId, sensorId, validated, value, valueText, createdAt, updatedAt }
      const measurements: Array<{
        id: string;
        time: string;
        deviceId: string;
        sensorId: string;
        validated: boolean;
        value: number;
        valueText?: string | null;
        createdAt: string;
        updatedAt: string;
        createdBy?: string | null;
        updatedBy?: string | null;
      }> = Array.isArray(response.data) ? response.data : [];

      // console.log("[useDeviceSensorManager] Measurements parsed count:", measurements.length);

      // IDs de sensores por tipo - ACTUALIZADOS con los IDs reales de la API
      // TODO: Estos IDs deberían venir de la configuración del dispositivo o del backend
      const SENSOR_IDS = {
        oxygen: "b7ef4fe7-c411-4335-bd50-f9d07258a6ca",      // Sensor de oxígeno real
        battery: "70695e70-d75f-40de-bd77-9a854367ed9f",     // Sensor de batería/voltaje real
        temperature: "b7ef4fe7-c411-4335-bd50-f9d07258a6ca", // Usar mismo sensor para temperatura si aplica
      };
      
      // Log para debug - mostrar los primeros 5 registros con sus sensorIds
      // console.log("[useDeviceSensorManager] Primeros 5 registros:", measurements.slice(0, 5).map(m => ({
      //   sensorId: m.sensorId,
      //   value: m.value,
      //   time: m.time
      // })));

      // Helpers de mapeo
      const temperatureData: Array<{ timestamp: string; temperature: number }> =
        [];
      const oxygenData: Array<{ timestamp: string; oxygen: number }> = [];
      const batteryData: Array<{
        timestamp: string;
        batteryLevel: number;
        voltage?: number;
      }> = [];

      // Log de los SENSOR_IDS que estamos buscando
      // console.log("[useDeviceSensorManager] SENSOR_IDS configurados:", SENSOR_IDS);
      
      // Log de todos los sensorIds únicos en las mediciones
      const uniqueSensorIds = [...new Set(measurements.map(m => m.sensorId))];
      // console.log("[useDeviceSensorManager] SensorIds únicos en mediciones:", uniqueSensorIds);

      // Mapeo dinámico: usar los primeros 2 sensores encontrados
      // Sensor 0 = Oxígeno (o temperatura si solo hay uno)
      // Sensor 1 = Batería/Voltaje
      const dynamicSensorIds = {
        sensor1: uniqueSensorIds[0] || "",  // Primer sensor -> Oxígeno
        sensor2: uniqueSensorIds[1] || "",  // Segundo sensor -> Batería
      };
      // console.log("[useDeviceSensorManager] Mapeo dinámico:", dynamicSensorIds);

      for (const m of measurements) {
        const ts = new Date(m.time).toISOString();
        
        // Usar mapeo dinámico basado en los sensores encontrados
        if (m.sensorId === dynamicSensorIds.sensor1) {
          // Primer sensor: tratar como oxígeno (valores típicos 0-30%)
          oxygenData.push({ timestamp: ts, oxygen: m.value });
          // También agregamos como temperatura para el chart combinado
          temperatureData.push({ timestamp: ts, temperature: m.value });
        } else if (m.sensorId === dynamicSensorIds.sensor2) {
          // Segundo sensor: tratar como batería/voltaje
          const voltage = m.value;
          batteryData.push({ timestamp: ts, batteryLevel: 0, voltage });
        }
      }

      // console.log("[useDeviceSensorManager] Datos procesados:", {
      //   temperatureCount: temperatureData.length,
      //   oxygenCount: oxygenData.length,
      //   batteryCount: batteryData.length
      // });

      // Ordenar por timestamp
      temperatureData.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      oxygenData.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      batteryData.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      // Combinar temperatura y oxígeno por timestamp exacto
      const oxygenMap = new Map<string, number>();
      oxygenData.forEach((o) => oxygenMap.set(o.timestamp, o.oxygen));
      const temperatureOxygenData: Array<{
        timestamp: string;
        temperature: number;
        oxygen: number;
      }> = [];
      temperatureData.forEach((t) => {
        const o = oxygenMap.get(t.timestamp);
        if (o !== undefined) {
          temperatureOxygenData.push({
            timestamp: t.timestamp,
            temperature: t.temperature,
            oxygen: o,
          });
        }
      });

      const lastValues = {
        temperature: temperatureData.at(-1)?.temperature,
        oxygen: oxygenData.at(-1)?.oxygen,
        batteryLevel: batteryData.at(-1)?.batteryLevel,
        voltage: batteryData.at(-1)?.voltage,
      };

      return {
        temperatureData,
        oxygenData,
        batteryData,
        temperatureOxygenData,
        lastValues,
      };
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error desconocido al cargar datos del dispositivo";
      error.value = errorMessage;
      console.error("Error loading device sensor data:", err);

      // Retornar datos vacíos en caso de error
      return {
        temperatureData: [],
        oxygenData: [],
        batteryData: [],
        temperatureOxygenData: [],
        lastValues: {},
      };
    } finally {
      loading.value = false;
    }
  };

  /**
   * Obtiene datos de crecimiento (si existe ese tipo de sensor)
   */
  const loadGrowthData = async (
    deviceId: string
  ): Promise<Array<{ date: string; growth: number; newRoots: number }>> => {
    try {
      const { deviceMeasurementsStore, deviceSettingsStore } = getStores();

      await deviceSettingsStore.getDeviceSettingsByDeviceId(deviceId);
      const deviceSettings = deviceSettingsStore.deviceSettings[0];

      if (!deviceSettings) return [];

      // Buscar sensores de crecimiento
      const growthSensors: string[] = [];
      deviceSettings.sensorSettings?.forEach(
        (sensorSetting: SensorSettings) => {
          const sensorField = sensorSetting.sensorId.toLowerCase();
          if (
            sensorField.includes("growth") ||
            sensorField.includes("crecimiento") ||
            sensorField.includes("root") ||
            sensorField.includes("raiz")
          ) {
            growthSensors.push(sensorSetting.sensorId);
          }
        }
      );

      if (growthSensors.length === 0) {
        // Sin sensores de crecimiento definidos: devolver vacío (no datos ficticios)
        return [];
      }

      // Cargar datos reales de crecimiento
      const growthDataRaw: Record<string, DeviceMeasurement[]> =
        await deviceMeasurementsStore.getDeviceMeasurementsBySensorIds(
          growthSensors
        );
      const growthData: Array<{
        date: string;
        growth: number;
        newRoots: number;
      }> = [];

      Object.values(growthDataRaw).forEach(
        (sensorData: DeviceMeasurement[]) => {
          sensorData.forEach((data: DeviceMeasurement) => {
            growthData.push({
              date: data.time.toISOString().split("T")[0],
              growth: data.value,
              newRoots: data.valueText ? parseInt(data.valueText) || 0 : 0,
            });
          });
        }
      );

      return growthData.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    } catch (err) {
      console.error("Error loading growth data:", err);
      return [];
    }
  };

  return {
    loading,
    error,
    loadDeviceSensorData,
    loadGrowthData,
    getSensorMapping,
  };
}
